import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    IconButton,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Alert
} from "@mui/material";
import { Image } from "lucide-react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { Header } from "../Header";
import SearchIcon from "@mui/icons-material/Search";
import { local_url, local_signage } from "../Const";

export const CreditApprove = () => {
    const [cnBatchNumber, setCnBatchNumber] = useState("");
    const [cnBatchOptions, setCnBatchOptions] = useState([]);
    const [batchData, setBatchData] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});
    const [footerRemarks, setFooterRemarks] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [action, setAction] = useState("");
    const [batchDetails, setBatchDetails] = useState(null);
    const [status, setStatus] = useState();
    const [fromDate, setFromDate] = useState(dayjs().subtract(15, 'day'));
    const [toDate, setToDate] = useState(dayjs());
    const [cnBatchDate, setCnBatchDate] = useState(null);
    const [cnType, setCnType] = useState("ALL");
    const [cnTypes, setCnTypes] = useState([]);
    const [customerTypes, setCustomerTypes] = useState([]);
    const [issueType, setIssueType] = useState("ALL");
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredBatchData, setFilteredBatchData] = useState([]);
    const [remarksError, setRemarksError] = useState({});
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState(null);

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem('AuthorizationToken');



   



    const checkImageUrl = async (url) => {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch {
            return false;
        }
    };


    const handleViewImage = async (batchNumber, customerId) => {
        setImageDialogOpen(true);
        setImageLoading(true);
        setImageError(null);
        setCurrentImage(null);

        try {
            const authToken = localStorage.getItem('AuthorizationToken');
            const response = await fetch(
                `${local_signage}/api/v1/fetchfile?batchNumber=${batchNumber}&customerId=${customerId}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (!response.ok) throw new Error('Failed to fetch file');
            const data = await response.json();
            setCurrentImage(data.fileUrl);
        } catch (error) {
            console.error('Error fetching file:', error);
            setImageError('Failed to load file. Please try again.');
        } finally {
            setImageLoading(false);
        }
    };


    // Fetch CN Batch options on component mount and when dates change
    // Fetch CN Batch options on component mount and when dates, issueType, or customerTypes change
    useEffect(() => {
        const fetchCNBatchOptions = async () => {
            const token = localStorage.getItem("AuthorizationToken");

            if (!token) {
                console.error("Authorization token is missing. Redirecting to login...");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
                return;
            }

            // Guard clause: Ensure required fields are available
            if (!cnType || !issueType || !fromDate || !toDate) {
                console.warn("Missing required parameters: CN Type, Issue Type, or Dates.");
                return;
            }

            try {
                const url = `${local_url}/api/CN/GetCNBatch/${cnType}/${issueType}/${fromDate.format('YYYYMMDD')}/${toDate.format('YYYYMMDD')}`;

                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Add the "All" option manually at the beginning
                const allOption = { cnBatchId: "All", remarks: "All Batches" };
                setCnBatchOptions([allOption, ...(response.data || [])]);
            } catch (error) {
                console.error("Error fetching CN Batch options:", error);

                if (error.response?.status === 401) {
                    localStorage.clear();
                    sessionStorage.clear();
                    alert("Session expired. Please log in again.");
                    window.location.href = "/login";
                } else {
                    setFetchError("Failed to fetch CN Batch options. Please try again later.");
                }
            }
        };

        fetchCNBatchOptions();
    }, [cnType, issueType, fromDate, toDate]);



    const validateRemarks = () => {
        const newRemarksError = {};
        let isValid = true;

        // Check remarks for selected rows
        Object.keys(selectedRows).forEach(rowId => {
            if (selectedRows[rowId]) {
                const row = batchData.find(item => item.id === parseInt(rowId));
                if (!row.userRemarks || row.userRemarks.trim() === '') {
                    isValid = false;
                }
            }
        });

        setRemarksError(newRemarksError);
        return isValid;
    };



    useEffect(() => {
        if (searchTerm) {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            const filtered = batchData.filter(row =>
                row.custId.toLowerCase().includes(lowercasedSearchTerm) ||
                (row.custName && row.custName.toLowerCase().includes(lowercasedSearchTerm)) ||
                (row.remarks && row.remarks.toLowerCase().includes(lowercasedSearchTerm))
            );
            setFilteredBatchData(filtered);
        } else {
            setFilteredBatchData(batchData);
        }
    }, [searchTerm, batchData]);



    useEffect(() => {
        const fetchCustomerTypes = () => {
            const token = localStorage.getItem('AuthorizationToken'); // Retrieve the token

            if (!token) {
                console.error("Authorization token is missing. Redirecting to login...");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
                return;
            }

            fetch(`${local_url}/api/CustomerList/GetCustomerTypes/C`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then((response) => {
                    if (response.status === 401) {
                        throw new Error("Unauthorized access. Session expired.");
                    }
                    if (!response.ok) {
                        throw new Error(`Failed to fetch customer types. Status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    setCustomerTypes(data.filter((item) => !item.deleted)); // Filter active items
                })
                .catch((error) => {
                    console.error("Error fetching customer types:", error);
                    if (error.message === "Unauthorized access. Session expired.") {
                        localStorage.clear();
                        sessionStorage.clear();
                        alert("Session expired. Please log in again.");
                        window.location.href = "/login";
                    }
                });
        };

        const fetchCNTypes = () => {
            const token = localStorage.getItem('AuthorizationToken'); // Retrieve the token

            if (!token) {
                console.error("Authorization token is missing. Redirecting to login...");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
                return;
            }

            fetch(`${local_url}/api/CN/GetCNTypes`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then((response) => {
                    if (response.status === 401) {
                        throw new Error("Unauthorized access. Session expired.");
                    }
                    if (!response.ok) {
                        throw new Error(`Failed to fetch CN types. Status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    setCnTypes(data.filter((item) => !item.deleted)); // Filter active items
                })
                .catch((error) => {
                    console.error("Error fetching CN types:", error);
                    if (error.message === "Unauthorized access. Session expired.") {
                        localStorage.clear();
                        sessionStorage.clear();
                        alert("Session expired. Please log in again.");
                        window.location.href = "/login";
                    }
                });
        };

        fetchCustomerTypes();
        fetchCNTypes();
    }, []);

    // Log the selected values for Issue Type, Customer Type, and CN Type
    useEffect(() => {
        console.log("Selected Issue Type: ", issueType);
        console.log("Selected Customer Types: ", customerTypes);
        console.log("Selected CN Type: ", cnType);
    }, [issueType, customerTypes, cnType]);









    const handleFetchDetails = async () => {
        setLoading(true);
        setFetchError(""); // Clear any existing error
        setSearchTerm(""); // Reset search term


        try {
            const token = localStorage.getItem("AuthorizationToken");
            if (!token) {
                setFetchError("Authorization token is missing.");
                setLoading(false);
                return;
            }

            let response;
            let transformedBatchData;

            if (cnBatchNumber === "All") {
                // Fetch data for ALL batches
                const allDataUrl = `${local_url}/api/CN/GetCNBatchLineList/${cnType}/${issueType}/${fromDate.format('YYYYMMDD')}/${toDate.format('YYYYMMDD')}`;


                response = await axios.get(allDataUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                console.log('-----', response.data);

                // Transform ALL batches data
                transformedBatchData = (response.data || []).map((item, index) => ({
                    id: index + 1,
                    cnBatchId: item.cnBatchId,
                    lineNumber: item.lineNumber,
                    custId: item.custId || "",
                    cnAmount: item.cnAmount || 0,
                    approvalStatus: item.approvalStatus || "",
                    creditNoteTypeId: "", // Not available in this response
                    customerTypeId: "", // Not available in this response
                    businessDate: "", // Not available in this response
                    remarks: item.remarks || item.remarks,
                    userRemarks: "",
                    approvingUserId: item.approvingUserId || "",
                    approvingDate: item.approvingDate ? dayjs(item.approvingDate).format('DD-MM-YYYY') : "",
                }));
                console.log('transformedBatchData', transformedBatchData);

            } else {
                // Fetch specific batch details
                const batchUrl = `${local_url}/api/CN/GetCNBatch/${cnBatchNumber}`;

                response = await axios.get(batchUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    const batchDetail = response.data[0];

                    // Format date function
                    const formatDate = (dateString) => {
                        return dateString ? dayjs(dateString).format('DD-MM-YYYY') : "";
                    };


                    // Transform specific batch details
                    transformedBatchData = (batchDetail.cnbatchdet || []).map((item, index) => ({
                        id: index + 1,
                        cnBatchId: batchDetail.cnBatchId,
                        lineNumber: item.lineNumber,
                        custId: item.custId || "",
                        cnAmount: item.cnAmount || 0,
                        approvalStatus: item.approvalStatus || "",
                        creditNoteTypeId: batchDetail.creditNoteTypeId || "",
                        customerTypeId: batchDetail.customerTypeId || "",
                        businessDate: formatDate(batchDetail.businessDate),
                        remarks: batchDetail.remarks || item.remarks,
                        userRemarks: "",
                        approvingUserId: item.approvingUserId || "",
                        approvingDate: formatDate(item.approvingDate),
                    }));
                } else {
                    setFetchError("No batch details found.");
                    transformedBatchData = [];
                }
            }

            console.log("Transformed Batch Data:", transformedBatchData);
            setBatchData(transformedBatchData);
            setFilteredBatchData(transformedBatchData);
        } catch (error) {
            if (error.response?.status === 401) {
                alert("Session expired. Please log in again.");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
            } else {
                setFetchError(error.response?.data?.message || "Error fetching data.");
                setBatchData([]);
            }
        } finally {
            setLoading(false);
        }
    };

    // Modify the CN Batch Number Select to only update the state
    const handleCNBatchChange = (event) => {
        const selectedValue = event.target.value;
        setCnBatchNumber(selectedValue);
        // Remove any previous batch data and errors
        setBatchData([]);
        setFetchError("");
    };



    const handleUserRemarksChange = (id, remarks) => {
        setBatchData(prevData =>
            prevData.map(item =>
                item.id === id ? { ...item, userRemarks: remarks } : item
            )
        );
        // Clear error when user starts typing
        if (remarksError[id]) {
            setRemarksError(prev => ({
                ...prev,
                [id]: undefined
            }));
        }
    };

    const handleRowSelection = (id) => {
        setSelectedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleApprove = () => {
        setAction("approve"); // Set action to 'approve'
        setOpenConfirmDialog(true); // Open confirmation dialog
    };

    const handleReject = () => {
        // Validate remarks before opening confirmation dialog
        if (validateRemarks()) {
            setAction("reject");
            setOpenConfirmDialog(true);
        } else {
            alert("Please provide remarks for all selected items before rejecting.");
        }
    };

    const handleConfirmAction = async () => {
        try {
            const token = localStorage.getItem("AuthorizationToken");
            if (!token) {
                alert("Authorization token is missing. Please log in again.");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
                return;
            }

            // Filter selected rows
            const selectedData = batchData.filter((row) => selectedRows[row.id]);

            // Map the data into request format
            const cnbatchdet = selectedData.map((row) => ({
                lineNumber: row.lineNumber,
                cnBatchId: row.cnBatchId,
                custId: row.custId,
                cnAmount: row.cnAmount,
                approvalStatus: action === "approve" ? "A" : "R", // A for approve, R for reject
                approvingUserId: userId,
                approvingDate: dayjs(),
                approverRemarks: row.userRemarks || footerRemarks || "",
                dml: "E",
                deleted: "N",
            }));

            const requestBody = {
                approvedUserId: userId,
                cnbatchdet,
            };

            const apiUrl = `${local_url}/api/CN/CNBatchApprove`;

            const response = await axios.put(apiUrl, requestBody, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            // Handle response
            if (response.status === 200) {
                alert(
                    action === "approve"
                        ? "Batch approved successfully"
                        : "Batch rejected successfully"
                );
            }

            // Reset form and state
            setSelectedRows({});
            setBatchData([]);
            setCnBatchNumber("");
            setFooterRemarks("");
        } catch (error) {
            if (error.response?.status === 401) {
                alert("Session expired. Please log in again.");
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/login";
            } else {
                console.error("Action failed:", error);
                setFetchError("Failed to process the action");
            }
        } finally {
            setOpenConfirmDialog(false); // Close the confirmation dialog
        }
    };



    const handleCancelAction = () => {
        setOpenConfirmDialog(false);
    };



    return (
        <>
            <Header />
            <Box sx={{ padding: 1, maxWidth: "100%", margin: "0" }}>
                <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "#2E7D32",
                        marginBottom: 2,
                    }}
                >
                    Credit Note Batch Approval
                </Typography>

                {/* First Row (Single Line) */}
                <Box sx={{ padding: { xs: 1, sm: 2 } }}>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 2 }}
                        sx={{
                            display: 'flex',
                            flexWrap: { xs: 'wrap', md: 'nowrap' },
                            alignItems: 'center',
                            '& .MuiGrid-item': {
                                width: { xs: '50%', sm: '50%', md: '50%' },
                                minWidth: { md: '100px' },
                                flexShrink: 1,
                                flexGrow: 1,
                            }
                        }}
                    >
                        {/* From Date */}
                        <Grid item>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(newDate) => setFromDate(newDate)}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                backgroundColor: "#F1F8E9",
                                                borderRadius: 1,
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* To Date */}
                        <Grid item>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="To Date"
                                    value={toDate}
                                    onChange={(newDate) => setToDate(newDate)}
                                    format="DD/MM/YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: {
                                                backgroundColor: "#F1F8E9",
                                                borderRadius: 1,
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* CN Type */}
                        <Grid item>
                            <TextField
                                label="CN Type"
                                variant="outlined"
                                size="small"
                                select
                                fullWidth
                                value={cnType}
                                onChange={(e) => setCnType(e.target.value)}
                                sx={{
                                    backgroundColor: "#F1F8E9",
                                    borderRadius: 1,
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                    },
                                }}
                            >
                                <MenuItem value="All">All</MenuItem>
                                {cnTypes.map((type) => (
                                    <MenuItem key={type.creditNoteTypeCode} value={type.creditNoteTypeCode}>
                                        {type.creditNoteTypeName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Customer Type */}
                        <Grid item>
                            <TextField
                                label="Customer Type"
                                variant="outlined"
                                size="small"
                                select
                                fullWidth
                                value={issueType}
                                onChange={(e) => setIssueType(e.target.value)}
                                sx={{
                                    backgroundColor: "#F1F8E9",
                                    borderRadius: 1,
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                    },
                                }}
                            >
                                <MenuItem value="All">All</MenuItem>
                                {customerTypes.map((type) => (
                                    <MenuItem key={type.customerTypeCode} value={type.customerTypeCode}>
                                        {type.customerTypeName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* CN Batch Number */}
                        <Grid item>
                            <FormControl
                                variant="outlined"
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '40px',
                                        backgroundColor: "#F1F8E9",
                                    },
                                }}
                            >
                                <InputLabel>CN Batch Number</InputLabel>
                                <Select
                                    value={cnBatchNumber}
                                    onChange={handleCNBatchChange}
                                    label="CN Batch Number"
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                                overflowY: "auto",
                                            },
                                        },
                                    }}
                                >
                                    {cnBatchOptions.map((batch) => (
                                        <MenuItem key={batch.cnBatchId} value={batch.cnBatchId}>
                                            {batch.cnBatchId === "All" ? "All Batches" : `${batch.cnBatchId} - ${batch.remarks}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Search Button */}
                        <Grid item sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { md: 'auto !important' } }}>
                            <IconButton
                                color="primary"
                                onClick={handleFetchDetails}
                                disabled={!cnBatchNumber || loading}
                                sx={{
                                    padding: 1,
                                    width: { xs: '20%', sm: 'auto' },
                                    height: '40px',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#bdbdbd',
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
                            </IconButton>
                        </Grid>
                    </Grid>
                </Box>




                {/* Fetch Error */}
                {fetchError && (
                    <Typography color="error" sx={{ marginBottom: 2 }}>
                        {fetchError}
                    </Typography>
                )}

                {/* Batch Data Table */}
                {batchData.length > 0 && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginBottom: 2
                            }}
                        >
                            <TextField
                                label="Search Customer ID, Name, or Remarks"
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ width: '300px' }}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                color: 'action.active',
                                                marginRight: 1
                                            }}
                                        />
                                    )
                                }}
                            />
                        </Box>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 3,
                                marginBottom: 2,
                                maxHeight: { xs: 300, sm: 400 },
                                overflow: "auto",
                            }}
                        >
                            <Table
                                sx={{
                                    minWidth: { xs: 500, sm: 700 },
                                    '& .MuiTableCell-root': {
                                        padding: { xs: '2px 10px', sm: '4px 20px' },
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Select</TableCell>
                                        <TableCell>Batch ID</TableCell>
                                        <TableCell>Line Number</TableCell>
                                        <TableCell>Customer ID</TableCell>
                                        <TableCell>Customer Name</TableCell>
                                        <TableCell>CN Amount</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Remarks</TableCell>
                                        <TableCell>Approval Remarks</TableCell>
                                        <TableCell>Image</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredBatchData.map((row) => (

                                        <TableRow key={row.id}>

                                            <TableCell>
                                                <Checkbox
                                                    checked={!!selectedRows[row.id]}
                                                    onChange={() => handleRowSelection(row.id)}
                                                    disabled={row.approvalStatus === "A" || row.approvalStatus === "R"}
                                                />
                                            </TableCell>
                                            <TableCell>{row.cnBatchId}</TableCell>
                                            <TableCell>{row.lineNumber}</TableCell>
                                            <TableCell>{row.custId}</TableCell>
                                            <TableCell>{row.custName || '-'} </TableCell>
                                            <TableCell>{row.cnAmount}</TableCell>


                                            <TableCell
                                                sx={{
                                                    color: row.approvalStatus === "A"
                                                        ? "green"
                                                        : row.approvalStatus === "R"
                                                            ? "red"
                                                            : "blue",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {row.approvalStatus === "A"
                                                    ? "Approved"
                                                    : row.approvalStatus === "R"
                                                        ? "Rejected"
                                                        : "Pending"}
                                            </TableCell>
                                            <TableCell>{row.remarks || "-"}</TableCell>

                                            <TableCell>
                                                <TextField
                                                    value={row.userRemarks}
                                                    onChange={(e) => handleUserRemarksChange(row.id, e.target.value)}
                                                    variant="outlined"
                                                    size="small"
                                                    multiline
                                                    rows={2}
                                                    fullWidth
                                                    disabled={row.approvalStatus === "A"}
                                                    error={!!remarksError[row.id]}
                                                    helperText={remarksError[row.id]}
                                                    sx={{
                                                        '& .MuiFormHelperText-root': {
                                                            color: 'error.main',
                                                            marginLeft: 0,
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <IconButton
                                                    onClick={() => handleViewImage(row.cnBatchId, row.custId)}
                                                    color="primary"
                                                    size="small"
                                                >
                                                    <Image size={20} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>


                       
                        <Dialog 
    open={imageDialogOpen} 
    onClose={() => {
        setImageDialogOpen(false);
        setImageError(null);
        setImageLoading(false);
        setCurrentImage(null);
    }}
    maxWidth="md"
    fullWidth
>
    <DialogContent>
        <Box sx={{ 
            height: '600px',  // Increased height for better PDF viewing
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            overflow: 'hidden' // Prevent scrolling issues
        }}>
            {imageLoading && (
                <CircularProgress />
            )}
            
            {imageError && (
                <Alert severity="error" sx={{ width: '100%' }}>
                    {imageError}
                </Alert>
            )}
            
            {!imageLoading && !imageError && currentImage && (
                <object
                    data={currentImage}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                >
                    <Alert severity="error" sx={{ width: '100%' }}>
                        Unable to display PDF. <a href={currentImage} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Click here</a> to open in new tab.
                    </Alert>
                </object>
            )}
        </Box>
    </DialogContent>
    <DialogActions>
        <Button onClick={() => setImageDialogOpen(false)} color="primary">
            Close
        </Button>
    </DialogActions>
</Dialog>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#F1F8E9",
                                padding: 2,
                                borderRadius: 1,
                                position: "sticky",
                                bottom: 0,
                                zIndex: 10,
                                boxShadow: "0 -2px 5px rgba(0,0,0,0.1)"
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ marginRight: 2 }}>
                                Total Amount: ₹{" "}
                                {filteredBatchData
                                    .filter((row) => selectedRows[row.id])
                                    .reduce((total, row) => total + (parseFloat(row.cnAmount) || 0), 0)}
                            </Typography>
                            <Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleApprove}
                                    sx={{ marginRight: 1 }}
                                    disabled={!Object.values(selectedRows).some((isSelected) => isSelected)} // Disable if no rows are selected
                                >
                                    Approve
                                </Button>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleReject}
                                    disabled={!Object.values(selectedRows).some((isSelected) => isSelected)} // Disable if no rows are selected
                                >
                                    Reject
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}

                <Dialog open={openConfirmDialog} onClose={handleCancelAction}>
                    <DialogTitle>
                        {action === "approve" ? "Confirm Approval" : "Confirm Rejection"}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Are you sure you want to {action} the selected batches?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelAction} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmAction} color="primary">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </>
    );

};



